import { createSlice } from '@reduxjs/toolkit'
import { STIKI_DEFAULT_COLOR } from '../../constants'

const initialState = {
  pagesPane: {
    isVisible: false,
  },
  network: {
    isFetching: false,
    hasError: false,
    errorMessage: '',
  },
  stikisMove: {
    isMoving: false,
    start: {
      x: 0,
      y: 0,
    },
    position: {
      x: 0,
      y: 0,
    },
  },
  resize: {
    isResizing: false,
    start: {
      x: 0,
      y: 0,
    },
    offset: {
      x: 0,
      y: 0,
    },
  },
  marquee: {
    isSelecting: false,
    start: {
      x: 0,
      y: 0,
    },
    box: {
      left: 0,
      top: 0,
      width: 0,
      height: 0,
    },
  },
  color: {
    current: STIKI_DEFAULT_COLOR,
    hasChanged: false,
  },
  isStatic: false,
  flash: {
    notice: "",
    alert: "",
  },
}

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Stikis move
    startStikisMove: (state, action) => {
      state.stikisMove.isMoving = true
      state.stikisMove.start = {
        x: action.payload.x,
        y: action.payload.y,
      }
    },
    updateStikisMove: (state, action) => {
      state.stikisMove.position = action.payload
    },
    endStikisMove: (state, action) => {
      state.stikisMove = initialState.stikisMove
    },
    // Resizing
    startResize: (state, action) => {
      state.resize.isResizing = true
      state.resize.start = {
        x: action.payload.x,
        y: action.payload.y,
      }
    },
    updateResize: (state, action) => {
      state.resize.offset = action.payload
    },
    endResize: (state, action) => {
      state.resize = initialState.resize
    },
    // Marquee selection
    startMarqueeSelect: (state, action) => {
      state.marquee.isSelecting = true
      state.marquee.start = action.payload
      state.marquee.box = {
        left: action.payload.x,
        top: action.payload.y,
        width: 0,
        height: 0,
      }
    },
    updateMarqueeSelect: (state, action) => {
      const { x, y } = action.payload
      const start = state.marquee.start

      state.marquee.box = {
        left: Math.min(start.x, x),
        top: Math.min(start.y, y),
        width: Math.abs(start.x - x),
        height: Math.abs(start.y - y),
      }
    },
    endMarqueeSelect: (state, action) => {
      state.marquee = initialState.marquee
    },
    // Misc
    showPagesPane: (state, action) => {
      state.pagesPane.isVisible = true
    },
    hidePagesPane: (state, action) => {
      state.pagesPane.isVisible = false
    },
    togglePagesPane: (state, action) => {
      state.pagesPane.isVisible = !state.pagesPane.isVisible
    },
    savePending: (state, action) => {
      // console.log('savePending()')
      state.network.isFetching = true
      state.network.hasError = false
      state.network.errorMessage = ''
    },
    saveFulfilled: (state, action) => {
      // console.log('saveFulfilled()')
      state.network = initialState.network
    },
    saveRejected: (state, action) => {
      // console.log('saveRejected()')
      const errorMessage = action.payload?.error || 'unknown error'

      state.network.isFetching = false
      state.network.hasError = true
      state.network.errorMessage = errorMessage
    },
    setUIColor: (state, action) => {
      const current = action.payload.current
      const hasChanged = action.payload.hasChanged
      state.color = {
        current,
        hasChanged,
      }
    },
    setIsStatic: (state, action) => {
      state.isStatic = action.payload
    },
    setFlashNotice: (state, action) => {
      state.flash.notice = action.payload
    },
    setFlashAlert: (state, action) => {
      state.flash.alert = action.payload
    },
    clearFlashNotice: (state) => {
      state.flash.notice = ""
    },
    clearFlashAlert: (state) => {
      state.flash.alert = ""
    }
  },
})

export const {
  // Stikis moving
  startStikisMove,
  updateStikisMove,
  endStikisMove,
  // Resize
  startResize,
  updateResize,
  endResize,
  // Marquee selection
  startMarqueeSelect,
  updateMarqueeSelect,
  endMarqueeSelect,
  // Misc
  showPagesPane,
  hidePagesPane,
  togglePagesPane,
  setUIColor,
  savePending,
  saveFulfilled,
  saveRejected,
  setIsStatic,
  setFlashNotice,
  setFlashAlert,
  clearFlashNotice,
  clearFlashAlert,
} = uiSlice.actions

export default uiSlice.reducer

// Stikis move

export const selectStikisMove = (state) => state.ui.stikisMove

export const selectStikisMovePosition = (state) => state.ui.stikisMove.position

// Resize

export const selectStikisResize = (state) => state.ui.resize

export const selectIsResizing = (state) => state.ui.resize.isResizing

export const selectResizeOffset = (state) => state.ui.resize.offset

// Marquee select

export const selectMarqueeIsSelecting = (state) => state.ui.marquee.isSelecting

export const selectMarqueeBox = (state) => state.ui.marquee.box

// Misc

export const selectUIColorCurrent = (state) => state.ui.color.current

export const selectUIColorHasChanged = (state) => state.ui.color.hasChanged

export const selectPagesPaneIsVisible = (state) => state.ui.pagesPane.isVisible

export const selectNetwork = (state) => state.ui.network

export const selectNetworkIsFetching = (state) => state.ui.network.isFetching

export const selectIsStatic = (state) => state.ui.isStatic

export const selectFlashNotice = (state) => state.ui.flash.notice

export const selectFlashAlert = (state) => state.ui.flash.alert

